// runBattles split across Web Workers (simWorker.js): one per spare core, up to 8. A 300-run odds check that took 5-9 s
// on the page's own thread (and froze it) is shared out, and the page stays responsive. Each warm worker holds its own
// copy of the sim and game data (~11 MB), and odds come in bursts (page load, a new tournament, a team card), so the
// pool shuts down after IDLE_MS with nothing to do and starts again on the next request (2026-11-02: eight idle
// workers held ~100 MB, four times the homepage itself).
// Without Worker support, or if a worker fails, it runs on the page as before. Same result shape as runBattles.
import { runBattles } from './battleSim.js';
import { prepTeam } from './simPrep.js';

// The site's sim buttons' battles per matchup (the bracket odds, the threat board, the Fight View, the predict game's sim
// pick): ±3 points at 50/50, ±2 at 90/10. battleConfig's 600 stays for the betting bot and the tools.
export const ODDS_RUNS = 300;
const MAX_WORKERS = 8;
const IDLE_MS = 30000;
let pool = null, nextId = 1, idleTimer = 0;
const pending = new Map();

function shutdown() {
  idleTimer = 0;
  if (pending.size || !pool) return;
  for (const w of pool) w.terminate();
  pool = null;
}
// Called whenever a job settles: once nothing is in flight, start the idle countdown.
function settled() {
  if (pending.size || !pool || !pool.length || idleTimer) return;
  idleTimer = setTimeout(shutdown, IDLE_MS);
}

function workers() {
  if (idleTimer) { clearTimeout(idleTimer); idleTimer = 0; }
  if (pool) return pool;
  pool = [];
  if (typeof Worker === 'undefined') return pool;
  const n = Math.max(1, Math.min(MAX_WORKERS, ((typeof navigator !== 'undefined' && navigator.hardwareConcurrency) || 4) - 1));
  try {
    for (let i = 0; i < n; i++) {
      const w = new Worker(new URL('./simWorker.js', import.meta.url), { type: 'module' });
      w.onmessage = (e) => { const p = pending.get(e.data.id); if (!p) return; pending.delete(e.data.id); if (e.data.error) p.reject(new Error(e.data.error)); else p.resolve(e.data.res); settled(); };
      w.onerror = () => { for (const [id, p] of pending) if (p.worker === w) { pending.delete(id); p.reject(new Error('sim worker failed')); } settled(); };
      pool.push(w);
    }
  } catch (e) { pool = []; }
  return pool;
}

// Plain copies for postMessage: Vue's reactive proxies can't be cloned.
const plain = (x) => (x == null ? x : JSON.parse(JSON.stringify(x)));

// The share-out's results, merged as one runBattles over all the runs.
export function mergeRuns(parts) {
  const n = parts.reduce((s, r) => s + r.n, 0), a = parts.reduce((s, r) => s + r.aWins, 0), b = parts.reduce((s, r) => s + r.bWins, 0);
  const surv = new Map();
  for (const r of parts) for (const u of r.unitStats) { const k = u.team + ' ' + u.name, s = surv.get(k) || { name: u.name, team: u.team, survived: 0 }; s.survived += u.survivalPct * r.n; surv.set(k, s); }
  return { n, aWins: a, bWins: b, draws: n - a - b, aWinPct: a / n, bWinPct: b / n, unitStats: [...surv.values()].map((s) => ({ name: s.name, team: s.team, survivalPct: s.survived / n })) };
}

// rosterA/rosterB: the raw tournament units. config: battleConfig plus terrain/tiles. data: the page's game data, for
// the on-page fallback (the workers build their own from the same snapshots).
export async function runBattlesParallel(rosterA, rosterB, config, { runs = config.runs, data } = {}) {
  const ws = workers();
  const onPage = () => runBattles(prepTeam(rosterA, data), prepTeam(rosterB, data), { ...config, runs });
  if (!ws.length) return onPage();
  const { terrain, tiles, ...rest } = config;
  const cfg = { ...plain(rest), terrain: plain(terrain), tiles: tiles ? { w: tiles.w, h: tiles.h, tiles: tiles.tiles } : tiles };
  const a = plain(rosterA), b = plain(rosterB), k = Math.min(ws.length, runs);
  try {
    const parts = await Promise.all(ws.slice(0, k).map((w, i) => new Promise((resolve, reject) => {
      const id = nextId++, share = Math.floor(runs / k) + (i < runs % k ? 1 : 0);
      pending.set(id, { resolve, reject, worker: w });
      w.postMessage({ id, rosterA: a, rosterB: b, config: cfg, runs: share });
    })));
    return mergeRuns(parts);
  } catch (e) {
    return onPage();
  }
}
